// Importing the sales spreadsheet: drop a CSV or Excel file, see what would happen — how many leads would be added, which rows
// are already leads, which could not be read, which columns were used — and only then add them. The file is read in the
// browser as base64 and sent twice at most: once as a dry run (nothing is written), once to commit. A duplicate is skipped
// and listed, never overwritten.

import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, StatCard, Table, useToast, type TableColumn } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { problemDetail, problemStatus } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { Notice } from '../../ui';
import { UploadDrop } from '../catalogue/UploadDrop';
import { useImportLeads } from './queries';

/** The most a file may be: five megabytes, as the api takes. */
export const IMPORT_MAX_BYTES = 5 * 1024 * 1024;

type Result = Awaited<ReturnType<ReturnType<typeof useImportLeads>['mutateAsync']>>;

interface Previewed {
  readonly fileName: string;
  readonly contentBase64: string;
  readonly result: Result;
}

/** A file's bytes as base64, read in the browser. */
export function readAsBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.onload = () => {
      const url = typeof reader.result === 'string' ? reader.result : '';
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

export interface ImportLeadsDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function ImportLeadsDialog({ open, onOpenChange }: ImportLeadsDialogProps): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg" dirty="auto" title={t('leads.import.title')} description={t('leads.import.description')}>
      {open ? <ImportFlow onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function ImportFlow({ onDone }: { readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const importLeads = useImportLeads();
  const [reading, setReading] = useState<string | undefined>(undefined);
  const [previewed, setPreviewed] = useState<Previewed | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  const words = (error: unknown): string => {
    const status = problemStatus(error);
    if (status === 422) return t('leads.import.unreadable');
    if (status === 400) return problemDetail(error) ?? t('leads.import.tooBig');
    return problems.text(error, { fallback: t('leads.import.failed') });
  };

  const choose = async (file: File | null) => {
    if (file === null) return;
    setFailure(undefined);
    setPreviewed(undefined);
    if (!/\.(csv|xlsx)$/i.test(file.name)) {
      setFailure(t('leads.import.wrongType'));
      return;
    }
    if (file.size > IMPORT_MAX_BYTES) {
      setFailure(t('leads.import.tooBig'));
      return;
    }
    setReading(file.name);
    try {
      const contentBase64 = await readAsBase64(file);
      const result = await importLeads.mutateAsync({ fileName: file.name, contentBase64, dryRun: true });
      setPreviewed({ fileName: file.name, contentBase64, result });
    } catch (error) {
      setFailure(words(error));
    } finally {
      setReading(undefined);
    }
  };

  const commit = async () => {
    if (previewed === undefined) return;
    setFailure(undefined);
    try {
      const done = await importLeads.mutateAsync({ fileName: previewed.fileName, contentBase64: previewed.contentBase64, dryRun: false });
      show({ tone: 'ok', title: t('leads.import.done', { count: done.added }) });
      onDone();
    } catch (error) {
      setFailure(words(error));
    }
  };

  return (
    <div className="console-form console-hq-import">
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      <UploadDrop
        label={t('leads.import.fileLabel')}
        title={t('leads.import.drop')}
        hint={t('leads.import.dropHint')}
        accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        disabled={reading !== undefined || importLeads.isPending}
        fileName={reading === undefined ? (previewed?.fileName ?? null) : t('leads.import.reading', { file: reading })}
        onFile={(file) => void choose(file)}
      />
      <details className="console-hq-details">
        <summary>{t('leads.import.columnsHelp')}</summary>
        <div className="console-hq-details__body">
          <p className="console-muted">{t('leads.import.columnsBody')}</p>
        </div>
      </details>

      {previewed === undefined ? null : <Preview result={previewed.result} />}

      <div className="console-form__actions">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('leads.import.cancel')}
        </Button>
        {previewed === undefined ? null : (
          <Button type="button" variant="primary" disabled={previewed.result.added === 0} busy={importLeads.isPending && reading === undefined} onClick={() => void commit()}>
            {t('leads.import.confirm', { count: previewed.result.added })}
          </Button>
        )}
      </div>
    </div>
  );
}

function Preview({ result }: { readonly result: Result }): ReactNode {
  const { t } = useTranslation('hq');
  const duplicateColumns: readonly TableColumn<Result['duplicates'][number]>[] = [
    { key: 'row', header: t('leads.import.row'), numeric: true, cell: (row) => row.row },
    { key: 'name', header: t('leads.import.name'), cell: (row) => row.businessName },
    { key: 'reason', header: t('leads.import.reason'), cell: (row) => row.reason },
  ];
  const refusedColumns: readonly TableColumn<Result['refused'][number]>[] = [
    { key: 'row', header: t('leads.import.row'), numeric: true, cell: (row) => row.row },
    { key: 'reason', header: t('leads.import.reason'), cell: (row) => row.reason },
  ];
  return (
    <div className="console-hq-import__result" aria-live="polite">
      <div className="console-hq-import__figures">
        <StatCard label={t('leads.import.total')} value={result.total} />
        <StatCard label={t('leads.import.willAdd')} value={result.added} />
        <StatCard label={t('leads.import.duplicatesCount')} value={result.duplicates.length} />
        <StatCard label={t('leads.import.refusedCount')} value={result.refused.length} tone={result.refused.length > 0 ? 'warn' : 'default'} />
      </div>
      {result.added === 0 ? <p className="console-muted">{t('leads.import.nothingToAdd')}</p> : null}
      <dl className="console-hq-import__columns">
        <div>
          <dt>{t('leads.import.matched')}</dt>
          <dd>{result.columns.matched.length === 0 ? t('leads.import.none') : result.columns.matched.join(', ')}</dd>
        </div>
        <div>
          <dt>{t('leads.import.ignored')}</dt>
          <dd>{result.columns.ignored.length === 0 ? t('leads.import.none') : result.columns.ignored.join(', ')}</dd>
        </div>
      </dl>
      {result.duplicates.length === 0 ? null : (
        <div className="console-table-card console-hq-import__table">
          <Table caption={t('leads.import.duplicates')} columns={duplicateColumns} rows={result.duplicates} rowKey={(row) => `d${row.row}`} />
        </div>
      )}
      {result.refused.length === 0 ? null : (
        <div className="console-table-card console-hq-import__table">
          <Table caption={t('leads.import.refused')} columns={refusedColumns} rows={result.refused} rowKey={(row) => `r${row.row}`} rowTone={() => 'warn'} />
        </div>
      )}
    </div>
  );
}
